// ============================================================================
// Shopee — Commerce capability glue.
//
// Bridges the Shopee-specific helpers (`products.ts` / `orders.ts` /
// `stock.ts` / `logistics.ts`) into the shape the capability registry
// expects (`AdapterMethods<'commerce'>`).
//
// Usage (registration site in `index.ts`):
//   import { buildShopeeCommerceMethods } from './capability-methods'
//   register('shopee', 'commerce', buildShopeeCommerceMethods({}))
//
// The `ctx` argument on each method is the `AdapterContext` the dispatcher
// hands in — carrying the decrypted access_token + per-instance config JSON.
// Shopee-specific config (shop_id, partner_id, partner_key) lives on
// `ctx.config`; we unpack it per call to avoid coupling the registry to
// Shopee specifics.
//
// Page tokens:
//   Shopee uses different pagination primitives for products (numeric
//   `offset`) and orders (opaque `cursor` + rolling 15-day windows). We hide
//   both behind an opaque base64-encoded `pageToken` so callers never see
//   Shopee internals. Decoding is strict — a malformed token throws
//   `INVALID_INPUT` instead of silently resetting to page 0.
// ============================================================================

import type { AdapterMethods, AdapterContext } from '../../services/capability-registry'
import type {
  CommerceOrder,
  CommerceOrderStatus,
  CommerceProduct,
  FetchOrdersArgs,
  FetchOrdersResult,
  FetchProductsArgs,
  FetchProductsResult,
  UpdateStockBatchResult,
} from '../../capabilities/commerce'
import {
  getShopeeItemBaseInfo,
  listShopeeItemList,
  SHOPEE_LIST_PAGE_DEFAULT,
  shopeeItemsToProducts,
} from './products'
import {
  canonicalToShopeeStatus,
  getShopeeOrderDetail,
  listShopeeOrders,
  SHOPEE_ORDER_PAGE_DEFAULT,
  SHOPEE_ORDER_WINDOW_MAX_MS,
  shopeeOrdersToCommerce,
} from './orders'
import { updateShopeeStock, updateShopeeStockBatch } from './stock'
import { cancelShopeeOrder, shipShopeeOrder } from './logistics'
import { hmacSha256Hex } from './signing'
import { ShopeeError } from './types'

// ============================================================================
// Dependencies
// ============================================================================

export interface ShopeeCommerceMethodsDeps {
  /** Injectable fetch for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
  /** Optional logger sink for non-fatal errors. Defaults to console.error. */
  logger?: {
    error?: (msg: string, extra?: Record<string, unknown>) => void
  }
}

interface ShopeeInstanceConfig {
  partner_id: string | number
  partner_key: string
  shop_id: string | number
  /** Default carrier for `fulfillOrder` when the caller doesn't override. */
  default_shipping_method?: string
}

// ============================================================================
// buildShopeeCommerceMethods
// ============================================================================

export function buildShopeeCommerceMethods(
  deps: ShopeeCommerceMethodsDeps = {},
): AdapterMethods<'commerce'> {
  const fetchImpl = deps.fetchImpl ?? fetch

  return {
    /**
     * `fetchProducts` transparently auto-splits the underlying
     * `get_item_base_info` calls into batches of ≤50 when more than one page
     * of ids needs detail (Issue #255 M1). Every batch consumes one Shopee
     * quota unit. Budget accordingly.
     */
    async fetchProducts(args: FetchProductsArgs, ctx: AdapterContext): Promise<FetchProductsResult> {
      const cfg = unpackConfig(ctx.config)
      const offset = await decodeProductPageToken(args.pageToken, cfg.partner_key)
      const pageSize =
        typeof args.limit === 'number' && args.limit > 0 ? args.limit : SHOPEE_LIST_PAGE_DEFAULT

      const list = await listShopeeItemList({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        offset,
        pageSize,
        ...(args.updatedSince
          ? { updateTimeFrom: Math.floor(new Date(args.updatedSince).getTime() / 1000) }
          : {}),
        fetchImpl,
      })

      if (list.itemIds.length === 0) {
        return { products: [] }
      }

      const details = await getShopeeItemBaseInfo({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        itemIds: list.itemIds,
        fetchImpl,
      })

      const result: FetchProductsResult = {
        products: shopeeItemsToProducts(details),
      }
      if (list.hasNextPage && typeof list.nextOffset === 'number') {
        result.nextPageToken = await encodeProductPageToken(
          list.nextOffset,
          cfg.partner_key,
        )
      }
      return result
    },

    async fetchProduct(args, ctx): Promise<CommerceProduct> {
      const cfg = unpackConfig(ctx.config)
      const itemId = Number(args.productId)
      if (!Number.isFinite(itemId)) {
        throw new ShopeeError('INVALID_INPUT', `productId ${args.productId} is not a finite number`)
      }
      const details = await getShopeeItemBaseInfo({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        itemIds: [itemId],
        fetchImpl,
      })
      if (details.length === 0) {
        throw new ShopeeError('ITEM_NOT_FOUND', `Shopee item ${args.productId} not found`)
      }
      const products = shopeeItemsToProducts(details)
      if (products.length === 0) {
        throw new ShopeeError('ITEM_NOT_FOUND', `Shopee item ${args.productId} failed to map`)
      }
      return products[0]!
    },

    /**
     * `fetchOrders` transparently auto-splits `get_order_detail` into batches
     * of ≤50 (Issue #255 M1). Each batch consumes one Shopee quota unit. In
     * addition, a multi-status filter fans out ONE `get_order_list` call per
     * status; those results are merged + deduped before the detail fetch.
     */
    async fetchOrders(args: FetchOrdersArgs, ctx: AdapterContext): Promise<FetchOrdersResult> {
      const cfg = unpackConfig(ctx.config)
      const now = Math.floor(Date.now() / 1000)
      const pageSize =
        typeof args.limit === 'number' && args.limit > 0 ? args.limit : SHOPEE_ORDER_PAGE_DEFAULT

      // Decode / initialise the pagination state — we thread the time window
      // + Shopee cursor through one opaque token so callers never have to
      // deal with Shopee's 15-day window cap.
      const pageState =
        (await decodeOrderPageToken(args.pageToken, cfg.partner_key)) ?? {
          timeFrom:
            args.updatedSince !== undefined
              ? Math.floor(new Date(args.updatedSince).getTime() / 1000)
              : now - Math.floor(SHOPEE_ORDER_WINDOW_MAX_MS / 1000),
          timeTo: now,
          cursor: undefined,
        }

      // Clamp `timeTo` to at most `timeFrom` + 15 days.
      const clampedTimeTo = Math.min(
        pageState.timeTo,
        pageState.timeFrom + Math.floor(SHOPEE_ORDER_WINDOW_MAX_MS / 1000),
      )

      // Statuses: Shopee accepts exactly ONE status per list call. If the
      // caller supplies multiple we fan out + merge, preserving the cursor
      // only for the FIRST status (the common 1-status path is the hot one;
      // multi-status sync is rare and a scheduler concern).
      const statuses: (CommerceOrderStatus | undefined)[] =
        Array.isArray(args.statuses) && args.statuses.length > 0 ? args.statuses : [undefined]

      const allSn: string[] = []
      let outCursor: string | undefined
      let outMore = false
      for (let i = 0; i < statuses.length; i++) {
        const status = statuses[i]
        const page = await listShopeeOrders({
          partnerId: cfg.partner_id,
          partnerKey: cfg.partner_key,
          accessToken: ctx.accessToken,
          shopId: cfg.shop_id,
          timeFrom: pageState.timeFrom,
          timeTo: clampedTimeTo,
          pageSize,
          ...(status ? { orderStatus: canonicalToShopeeStatus(status) } : {}),
          // The opaque cursor is only valid on the ORIGINAL status query; we
          // reset it for subsequent statuses to avoid Shopee returning a 400.
          ...(i === 0 && pageState.cursor ? { cursor: pageState.cursor } : {}),
          fetchImpl,
        })
        for (const sn of page.orderSnList) allSn.push(sn)
        if (i === 0) {
          outCursor = page.nextCursor
          outMore = page.more
        }
      }

      if (allSn.length === 0) {
        return { orders: [] }
      }

      // Issue #255 L3: the multi-status fan-out above can return the SAME
      // `order_sn` twice when the order's status changed mid-window (e.g.
      // it flipped from UNPAID to READY_TO_SHIP between the two list calls).
      // Dedup before the get_order_detail batch — otherwise we spend a
      // Shopee API quota unit on the same order twice AND emit a duplicate
      // `commerce.order.updated` event downstream.
      const uniqueSn = Array.from(new Set(allSn))

      const raws = await getShopeeOrderDetail({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        orderSnList: uniqueSn,
        fetchImpl,
      })

      const orders = shopeeOrdersToCommerce(raws)

      // Round 1 H2 fix: when the caller's requested window exceeds Shopee's
      // 15-day cap, Shopee is done with the CURRENT window (`outMore=false`)
      // but rows in `(clampedTimeTo, pageState.timeTo]` were NEVER queried.
      // Emit a continuation token that slides the window forward so the next
      // page call covers them.
      const result: FetchOrdersResult = { orders }
      if (outMore && outCursor) {
        // Common case: still paginating inside the current 15-day window.
        result.nextPageToken = await encodeOrderPageToken(
          {
            timeFrom: pageState.timeFrom,
            timeTo: clampedTimeTo,
            cursor: outCursor,
          },
          cfg.partner_key,
        )
      } else if (clampedTimeTo < pageState.timeTo) {
        // Window was truncated — advance to the next 15-day slice.
        result.nextPageToken = await encodeOrderPageToken(
          {
            timeFrom: clampedTimeTo,
            timeTo: pageState.timeTo,
            // cursor omitted — fresh window, first page.
          },
          cfg.partner_key,
        )
      }
      return result
    },

    async fetchOrder(args, ctx): Promise<CommerceOrder> {
      const cfg = unpackConfig(ctx.config)
      const raws = await getShopeeOrderDetail({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        orderSnList: [args.orderId],
        fetchImpl,
      })
      if (raws.length === 0) {
        throw new ShopeeError('ORDER_NOT_FOUND', `Shopee order ${args.orderId} not found`)
      }
      const orders = shopeeOrdersToCommerce(raws)
      if (orders.length === 0) {
        throw new ShopeeError('ORDER_NOT_FOUND', `Shopee order ${args.orderId} failed to map`)
      }
      return orders[0]!
    },

    async updateStock(args, ctx): Promise<void> {
      const cfg = unpackConfig(ctx.config)
      await updateShopeeStock({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        itemId: args.productId,
        stockList: [
          {
            ...(args.variantId ? { modelId: args.variantId } : {}),
            stock: args.quantity,
          },
        ],
        fetchImpl,
      })
    },

    async updateStockBatch(args, ctx): Promise<UpdateStockBatchResult> {
      const cfg = unpackConfig(ctx.config)
      return updateShopeeStockBatch({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        updates: args.updates,
        fetchImpl,
      })
    },

    async fulfillOrder(args, ctx): Promise<void> {
      const cfg = unpackConfig(ctx.config)
      const carrier = args.carrier || cfg.default_shipping_method
      if (!carrier) {
        throw new ShopeeError(
          'INVALID_INPUT',
          'fulfillOrder: carrier is required (or set `default_shipping_method` on instance config)',
        )
      }
      await shipShopeeOrder({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        orderSn: args.orderId,
        trackingNumber: args.trackingNumber,
        carrierShippingMethod: carrier,
        fetchImpl,
      })
    },

    async cancelOrder(args, ctx): Promise<void> {
      const cfg = unpackConfig(ctx.config)
      await cancelShopeeOrder({
        partnerId: cfg.partner_id,
        partnerKey: cfg.partner_key,
        accessToken: ctx.accessToken,
        shopId: cfg.shop_id,
        orderSn: args.orderId,
        cancelReason: args.reason,
        fetchImpl,
      })
    },
  }
}

// ============================================================================
// Config unpacking
// ============================================================================

function unpackConfig(raw: unknown): ShopeeInstanceConfig {
  let obj: Record<string, unknown> | null = null
  if (raw && typeof raw === 'string') {
    try {
      obj = JSON.parse(raw) as Record<string, unknown>
    } catch {
      obj = null
    }
  } else if (raw && typeof raw === 'object') {
    obj = raw as Record<string, unknown>
  }
  if (!obj) {
    throw new ShopeeError('INVALID_INPUT', 'Shopee instance config is missing or unparseable')
  }
  const partner_id = obj.partner_id
  const partner_key = obj.partner_key
  const shop_id = obj.shop_id
  if (
    (typeof partner_id !== 'string' && typeof partner_id !== 'number') ||
    typeof partner_key !== 'string' ||
    partner_key.length === 0 ||
    (typeof shop_id !== 'string' && typeof shop_id !== 'number')
  ) {
    throw new ShopeeError(
      'INVALID_INPUT',
      'Shopee instance config must include partner_id + partner_key + shop_id',
    )
  }
  const out: ShopeeInstanceConfig = {
    partner_id,
    partner_key,
    shop_id,
  }
  if (typeof obj.default_shipping_method === 'string' && obj.default_shipping_method.length > 0) {
    out.default_shipping_method = obj.default_shipping_method
  }
  return out
}

// ============================================================================
// Page token encoding (opaque base64url + HMAC signature).
//
// Issue #255 H1: the previous plain base64 JSON scheme let a caller forge an
// arbitrary `timeFrom` / `timeTo` / `cursor` — which, combined with the
// `fetchOrders` fan-out, would let them probe Shopee's 15-day window
// arbitrarily by crafting new tokens. Shopee rejects malformed cursors, so
// the blast radius is small, but the adapter contract is that callers must
// NEVER see or modify these values — and a sig makes that invariant
// enforceable.
//
// Wire format:  `<base64url(JSON)>.<hex-hmac-sha256(canonicalJson, partnerKey)>`
// Sig algorithm: HMAC-SHA256, lowercase hex, same primitive as `signing.ts`.
// ============================================================================

interface ProductPageState {
  offset: number
}

interface OrderPageState {
  timeFrom: number
  timeTo: number
  cursor?: string
}

async function encodeProductPageToken(
  offset: number,
  partnerKey: string,
): Promise<string> {
  const state: ProductPageState = { offset }
  return signPageToken(state, partnerKey)
}

async function decodeProductPageToken(
  token: string | undefined,
  partnerKey: string,
): Promise<number> {
  if (!token) return 0
  const parsed = await verifyPageToken<ProductPageState>(token, partnerKey)
  return typeof parsed.offset === 'number' && parsed.offset >= 0 ? parsed.offset : 0
}

async function encodeOrderPageToken(
  state: OrderPageState,
  partnerKey: string,
): Promise<string> {
  return signPageToken(state, partnerKey)
}

async function decodeOrderPageToken(
  token: string | undefined,
  partnerKey: string,
): Promise<OrderPageState | null> {
  if (!token) return null
  const parsed = await verifyPageToken<OrderPageState>(token, partnerKey)
  if (
    typeof parsed.timeFrom !== 'number' ||
    typeof parsed.timeTo !== 'number' ||
    parsed.timeTo < parsed.timeFrom
  ) {
    throw new ShopeeError('INVALID_INPUT', 'invalid pageToken')
  }
  return parsed
}

/**
 * Sign + base64url-encode a page-state object. Canonical JSON is
 * `JSON.stringify(state)` with the keys in insertion order — the callers build
 * state literally so key order is stable by construction. A downstream
 * refactor that changes field order would (intentionally) invalidate any
 * in-flight tokens.
 */
async function signPageToken(state: unknown, partnerKey: string): Promise<string> {
  if (typeof partnerKey !== 'string' || partnerKey.length === 0) {
    throw new ShopeeError(
      'INVALID_INPUT',
      'signPageToken: partnerKey is required',
    )
  }
  const json = JSON.stringify(state)
  const payload = b64urlEncode(json)
  const sig = await hmacSha256Hex(partnerKey, json)
  return `${payload}.${sig}`
}

/**
 * Verify + decode a signed page token. Throws `ShopeeError('INVALID_INPUT')`
 * on any of: missing dot separator, bad base64, sig mismatch, non-JSON payload.
 *
 * Sig comparison is constant-time via `timingSafeEqualHex`.
 */
async function verifyPageToken<T>(token: string, partnerKey: string): Promise<T> {
  if (typeof partnerKey !== 'string' || partnerKey.length === 0) {
    throw new ShopeeError(
      'INVALID_INPUT',
      'verifyPageToken: partnerKey is required',
    )
  }
  const dotIdx = token.indexOf('.')
  if (dotIdx <= 0 || dotIdx >= token.length - 1) {
    throw new ShopeeError('INVALID_INPUT', 'invalid pageToken')
  }
  const payloadB64 = token.slice(0, dotIdx)
  const sig = token.slice(dotIdx + 1)

  let json: string
  try {
    json = b64urlDecode(payloadB64)
  } catch {
    throw new ShopeeError('INVALID_INPUT', 'invalid pageToken')
  }

  const expected = await hmacSha256Hex(partnerKey, json)
  if (!timingSafeEqualHex(sig, expected)) {
    throw new ShopeeError('INVALID_INPUT', 'invalid pageToken')
  }

  try {
    return JSON.parse(json) as T
  } catch {
    throw new ShopeeError('INVALID_INPUT', 'invalid pageToken')
  }
}

/** Timing-safe compare of two hex strings. Short-circuits on length mismatch. */
function timingSafeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  }
  return diff === 0
}

function b64urlEncode(s: string): string {
  const bytes = new TextEncoder().encode(s)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function b64urlDecode(s: string): string {
  const normalized = s.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
  const bin = atob(padded)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new TextDecoder().decode(bytes)
}
